import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import FilterList from '@/components/Filter/FilterList/FilterList';
import log from 'loglevel';
import ExportJobTable from '@/components/ExportJobTable/ExportJobTable';
import { useMessageQueue } from '@erickson/components';
import { ListService } from '@/service/list/ListService';
import { useServices } from '@/components/ServiceContext';
import useFilterSelections from '@/pages/hooks/useFilterSelections';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import Typography from '@mui/material/Typography';
import { ListType } from '@/service/ExportJobService.ts';
import { useLocation } from 'react-router-dom';
import { FilterSelections } from '@lambda-layer/types/filter.ts';
import { reportDefinitions } from '@lambda-layer/constants/reportDefinitions';

const GenericReportPage: React.FC<{
  exportReportName: (filterSelections: FilterSelections) => string;
  listType: ListType;
  listService: ListService<unknown>;
  filterDefinitions: FilterDefinition[];
}> = ({ exportReportName, listType, listService, filterDefinitions }) => {
  const location = useLocation();

  const { exportJobService, routeService /*, exclusionCacheService */ } =
    useServices();

  // const exclusions = exclusionCacheService.getExclusions(listType);

  const [selectedFormat, setSelectedFormat] = useState<'xlsx' | 'csv'>('csv');

  const reportRoute = routeService.routes?.find(
    r => r.path === location.pathname
  );

  const reportName = React.useMemo(() => {
    const def = reportDefinitions.find(r => r.id === reportRoute?.id);
    return def?.reportName || reportRoute?.label || '';
  }, [reportRoute?.id, reportRoute?.label]);

  const {
    filterSelections,
    fieldSelectionCountMap,
    loading,
    updateSelection,
    handleFocusChange,
    handleChange,
    calculateFilterCounts,
  } = useFilterSelections(
    listService,
    filterDefinitions,
    listType
    /*, exclusions*/
  );

  const messageQueue = useRef(useMessageQueue()).current;

  const handleRunReport = async (
    exportName: string,
    reportId: string,
    filterSelections: FilterSelections,
    format: 'xlsx' | 'csv'
  ) => {
    try {
      await exportJobService.createExportJob(
        listType,
        exportName,
        filterDefinitions,
        filterSelections,
        reportId,
        format
      );
      messageQueue.addMessage({
        text: 'Report job created successfully',
        severity: 'success',
      });
    } catch (error) {
      log.error('Error creating report job:', error);
      messageQueue.addMessage({
        text: 'Error creating the report job',
        severity: 'error',
      });
    }
  };

  const handleFormatChange = (event: SelectChangeEvent<'xlsx' | 'csv'>) => {
    const format = event.target.value as 'xlsx' | 'csv';
    setSelectedFormat(format);
  };

  return (
    <>
      <Box ml={3} mr={3} mt={2}>
        <Grid container>
          {/* Main Filter Section */}
          <Grid size={6}>
            <Typography variant={'h2'} mb={3} fontWeight={400}>
              {reportName}
            </Typography>
            <FilterList
              filterDefinitions={filterDefinitions}
              filterSelections={filterSelections}
              filterCounts={fieldSelectionCountMap}
              loading={loading}
              updateSelection={updateSelection}
              onFocusChange={handleFocusChange}
              onChange={handleChange}
              calculateFilterCounts={calculateFilterCounts}
              allowSaveFilters={false}
            />
            <Box sx={{ opacity: 0.5 }} ml={3} mr={3} mt={3} mb={2}>
              <hr />
            </Box>
            <Grid container spacing={2}>
              <Grid>
                <Button
                  variant={'contained'}
                  onClick={() => {
                    if (reportRoute?.id) {
                      void handleRunReport(
                        exportReportName(filterSelections),
                        reportRoute.id,
                        filterSelections,
                        selectedFormat
                      );
                    }
                  }}
                >
                  Run Report
                </Button>
              </Grid>
              <Grid>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="export-format-label">Format</InputLabel>
                  <Select
                    labelId="export-format-label"
                    id="export-format-select"
                    value={selectedFormat}
                    label="Format"
                    onChange={handleFormatChange}
                  >
                    <MenuItem value="xlsx">XLSX</MenuItem>
                    <MenuItem value="csv">CSV</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={6}>
            <ExportJobTable
              listType={listType}
              reportId={reportRoute?.id}
              filterDefinitions={filterDefinitions}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default GenericReportPage;
